<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import SettingsToggleSection from 'next/Settings/SettingsToggleSection.vue';

import Dashboard from './Dashboard.vue';
import EvaluationsList from './EvaluationsList.vue';
import CriteriaManager from './CriteriaManager.vue';
import RecommendationsList from './RecommendationsList.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const subTabs = [
  {
    key: 'dashboard',
    name: t('WGPT_BOTS.EDIT.TABS.QUALITY.DASHBOARD.TITLE'),
    icon: 'i-lucide-layout-dashboard',
  },
  {
    key: 'evaluations',
    name: t('WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.TITLE'),
    icon: 'i-lucide-clipboard-list',
  },
  {
    key: 'criteria',
    name: t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.TITLE'),
    icon: 'i-lucide-list-checks',
  },
  {
    key: 'recommendations',
    name: t('WGPT_BOTS.EDIT.TABS.QUALITY.RECOMMENDATIONS.TITLE'),
    icon: 'i-lucide-lightbulb',
  },
];

const activeTab = ref('dashboard');

const botId = computed(() => props.bot.id);
const evalEnabled = computed({
  get: () => props.bot.eval_enabled,
  set: value => {
    store.dispatch('wgptBots/update', {
      id: botId.value,
      eval_enabled: value,
    });
  },
});
</script>

<template>
  <div class="flex flex-col h-full max-w-7xl mx-auto w-full px-6">
    <div class="pt-8">
      <SettingsToggleSection
        v-model="evalEnabled"
        :header="t('WGPT_BOTS.EDIT.TABS.QUALITY.EVAL_ENABLED')"
        :description="t('WGPT_BOTS.EDIT.TABS.QUALITY.DESCRIPTION')"
      />
    </div>
    <div class="my-4 h-px bg-n-weak" />
    <div class="flex flex-1 min-h-0">
      <nav class="w-52 flex-shrink-0 border-e border-n-weak px-4 py-4">
        <div class="flex flex-col gap-1">
          <button
            v-for="tab in subTabs"
            :key="tab.key"
            class="flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors w-full text-start"
            :class="
              activeTab === tab.key
                ? 'bg-n-alpha-3 text-n-slate-12 font-medium'
                : 'text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="activeTab = tab.key"
          >
            <Icon
              :icon="tab.icon"
              class="size-4 flex-shrink-0"
              :class="
                activeTab === tab.key ? 'text-n-slate-12' : 'text-n-slate-10'
              "
            />
            {{ tab.name }}
          </button>
        </div>
      </nav>

      <div class="flex-1 overflow-auto px-6 py-6">
        <Dashboard
          v-if="activeTab === 'dashboard'"
          :bot="bot"
          :bot-id="botId"
          @navigate-to="activeTab = $event"
        />
        <EvaluationsList
          v-else-if="activeTab === 'evaluations'"
          :bot-id="botId"
        />
        <CriteriaManager v-else-if="activeTab === 'criteria'" :bot-id="botId" />
        <RecommendationsList
          v-else-if="activeTab === 'recommendations'"
          :bot-id="botId"
        />
      </div>
    </div>
  </div>
</template>
